<script setup>
import { computed, nextTick, ref, watch } from "vue";
import { useStore } from "vuex";

// A few words the genie can't read anywhere else. Past messages and files belong in the
// sources above, so this stays small until something's typed.
const MAX_CONTEXT = 10000;

const store = useStore();
const stored = computed(() => store.state.context.context);
const isMobile = computed(() => store.state.isMobile);

const text = ref(stored.value);
const textarea = ref(null);

watch(text, (value) => {
  if (value.length <= MAX_CONTEXT) {
    store.dispatch("context/addContext", value);
  }
});
// Restored pages and remixes set the context from outside
watch(stored, (value) => {
  if (value !== text.value) text.value = value;
});

const tooLong = computed(() => text.value.length > MAX_CONTEXT);
const nearLimit = computed(() => text.value.length > MAX_CONTEXT * 0.8);

// Grows with what's typed, up to about half the screen
const grow = () => {
  const box = textarea.value;
  if (!box) return;
  box.style.height = "auto";
  box.style.height = `${Math.min(
    box.scrollHeight + 2,
    window.innerHeight * 0.55
  )}px`;
};
watch(text, () => nextTick(grow));

// Keep the box clear of the on-screen keyboard on phones
const onFocus = () => {
  if (isMobile.value) {
    textarea.value?.scrollIntoView({ behavior: "smooth", block: "center" });
  }
};
</script>

<template>
  <section>
    <label
      for="shepherd-target-context"
      class="mb-1 block text-sm font-bold text-primary"
    >
      Notes <span class="font-normal text-secondary">(optional)</span>
    </label>
    <textarea
      id="shepherd-target-context"
      ref="textarea"
      v-model="text"
      rows="2"
      class="block min-h-[4.5rem] w-full resize-none rounded-xl border border-gray-200 bg-white px-4 py-3 text-base text-primary placeholder:text-secondaryLight focus:border-accentPrimary focus:outline-none"
      :class="{ 'border-red-500': tooLong }"
      placeholder="Anything it can't read elsewhere, like who it's for or a date to include"
      :aria-invalid="tooLong"
      enterkeyhint="done"
      @focus="onFocus"
      @input="grow"
    ></textarea>
    <p
      v-if="nearLimit"
      class="mt-1 text-right text-xs"
      :class="tooLong ? 'font-bold text-red-500' : 'text-secondary'"
      role="status"
    >
      {{ text.length.toLocaleString() }} / {{ MAX_CONTEXT.toLocaleString() }}
    </p>
    <p v-if="tooLong" class="mt-1 text-sm font-medium text-red-500">
      That's more than fits here. Use Paste above instead and it's read as a
      document.
    </p>
  </section>
</template>
